import { api, hasApiBaseUrl, useMockFallback, withFallback } from "./api/client";
import { licitaciones as mockLicitaciones, estadoLabels, estadoColors } from "@/lib/mock-data";
import type {
  Licitacion,
  LicitacionEstado,
  LicitacionListResponse,
  Prioridad,
  TenantTracking,
} from "@/types";

export interface ListParams {
  estado?: LicitacionEstado;
  estadoMP?: string;
  prioridad?: Prioridad;
  responsable?: string;
  region?: string;
  organismo?: string;
  categoria?: string;
  tipoMP?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  montoMin?: number;
  montoMax?: number;
  q?: string;
  minScore?: number;
  soloTrackeadas?: boolean;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface UpdateTrackingInput {
  estadoPipeline?: LicitacionEstado;
  prioridad?: Prioridad;
  responsable?: string;
  notas?: string;
}

export interface FacetValue {
  value: string;
  count: number;
}

export interface Facets {
  tipoMP: FacetValue[];
  estadoMP: FacetValue[];
  region: FacetValue[];
  organismo: FacetValue[];
}

const emptyFacets: Facets = { tipoMP: [], estadoMP: [], region: [], organismo: [] };


export const licitacionesService = {
  list: (params: ListParams = {}) =>
    withFallback<LicitacionListResponse>(
      async () =>
        (
          await api.get<LicitacionListResponse>("/licitaciones", {
            params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)),
          })
        ).data,
      () => ({
        total: mockLicitaciones.length,
        page: 1,
        pageSize: mockLicitaciones.length,
        data: mockLicitaciones as unknown as Licitacion[],
      }),
    ),

  getById: async (id: string) => {
    if (hasApiBaseUrl) {
      return (await api.get<Licitacion>(`/licitaciones/${id}`)).data;
    }
    if (useMockFallback) {
      return mockLicitaciones.find((l) => l.id === id) as unknown as Licitacion | undefined;
    }
    throw new Error("API_BASE_URL no configurada y mock fallback deshabilitado");
  },

  track: (id: string) =>
    withFallback<TenantTracking>(
      async () => (await api.post<TenantTracking>(`/licitaciones/${id}/track`)).data,
      () => ({
        id: "mock-tracking",
        estadoPipeline: "detectada" as const,
        prioridad: "media" as const,
        responsable: null,
        scoreIA: 0,
        notas: null,
        postuladaAt: null,
      }),
    ),

  updateTracking: (id: string, input: UpdateTrackingInput) =>
    withFallback<TenantTracking>(
      async () => (await api.patch<TenantTracking>(`/licitaciones/${id}/tracking`, input)).data,
      () => ({
        id: "mock-tracking",
        estadoPipeline: input.estadoPipeline ?? ("detectada" as const),
        prioridad: input.prioridad ?? ("media" as const),
        responsable: input.responsable ?? null,
        scoreIA: 0,
        notas: input.notas ?? null,
        postuladaAt: input.estadoPipeline === "postulada" ? new Date().toISOString() : null,
      }),
    ),

  /** Carga masiva para Tarjetas/Calendario: una sola petición contra la base local. */
  board: (params: ListParams = {}) =>
    withFallback<{ total: number; truncado: boolean; data: Licitacion[] }>(
      async () =>
        (
          await api.get<{ total: number; truncado: boolean; data: Licitacion[] }>(
            "/licitaciones/board",
            {
              params: Object.fromEntries(
                Object.entries(params).filter(([, v]) => v !== undefined),
              ),
            },
          )
        ).data,
      () => ({
        total: mockLicitaciones.length,
        truncado: false,
        data: mockLicitaciones as unknown as Licitacion[],
      }),
    ),

  facets: () =>

    withFallback<Facets>(
      async () => (await api.get<Facets>("/licitaciones/facets")).data,
      () => emptyFacets,
    ),

  /** Frescura de los datos maestros (última sincronización con Mercado Público). */
  freshness: () =>
    withFallback<{ ultimaActualizacion: string | null; total: number }>(
      async () =>
        (await api.get<{ ultimaActualizacion: string | null; total: number }>(
          "/licitaciones/data-freshness",
        )).data,
      () => ({ ultimaActualizacion: null, total: 0 }),
    ),

  export: async (formato: "xlsx", params: ListParams) => {
    const response = await api.get("/licitaciones/export", {
      params: { formato, ...Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = `licitaciones-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  labels: () => estadoLabels,
  colors: () => estadoColors,
};

export type LicitacionesService = typeof licitacionesService;
